import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Grid, Typography, InputBase, Popover } from '@quillbot/design-system';
import { ArrowDropDown } from '@mui/icons-material';
import { getCgBreakpointDown, getCgBreakpointUp } from 'src/citation-generator/utils';
import { Months } from 'src/citation-generator/constants';
import { IDate } from 'src/citation-generator/types';
import { EditFormAction, FormValueKeys } from '../types';
import { useFieldConfig, useFieldValidator } from '../config';
import { useEditFormDispatch, useEditFormState } from '../context';

const useDateTextWithLabelStyle = makeStyles((theme) => ({
  baseRoot: {
    [getCgBreakpointDown(theme)]: {
      flexDirection: 'column',
      marginBottom: '0 !important',
    },
  },
  inputsWrapper: {
    width: '76%',
    [getCgBreakpointDown(theme)]: {
      width: '100%',
      marginTop: 4,
    },
  },
  inputContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    height: '30px',
    position: 'relative',
  },
  input: {
    padding: '0px 10px',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    width: '100%',
    [getCgBreakpointUp(theme)]: {
      '& > input::placeholder': {
        opacity: '0',
      },
      '& > .placeholder': {
        display: 'none',
      },
    },
    '& > .placeholder': {
      opacity: 0.45,
    },
  },
  label: {
    float: 'right',
    fontSize: '12px',
    color: theme.palette.text.primary,
    marginTop: 18,
    [getCgBreakpointDown(theme)]: {
      float: 'unset',
      marginTop: 12,
    },
  },
  labelContainer: {
    width: '24%',
    paddingRight: '8px',
    [getCgBreakpointDown(theme)]: {
      width: '100%',
    },
  },
  warning: {
    border: `1px solid ${theme.palette.secondary.main}`,
  },
  inputContainerOuterContainer: {
    borderRadius: 3,
    width: 'calc(33.33% - 2.33px)',
    height: '46px',
  },
  inputLabel: {
    fontSize: '12px',
    marginLeft: '5px',
    [getCgBreakpointDown(theme)]: {
      display: 'none',
    },
  },
  menuItemContainer: {
    maxHeight: 140,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  menuItem: {
    alignItems: 'center',
    display: 'flex',
    cursor: 'pointer',
    height: 25,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  disabledMenu: {
    backgroundColor: theme.palette.background.default,
  },
  monthInputDisabled: {
    color: theme.palette.text.primary,
    WebkitTextFillColor: `${theme.palette.text.primary} !important`,
  },
}));

export function DateFieid(props: {
  rootClass?: string;
  valueKey: FormValueKeys;
  setter: (date: IDate) => EditFormAction;
}) {
  const { valueKey, setter } = props;
  const toDay = new Date();
  const classes = useDateTextWithLabelStyle();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { label, show } = useFieldConfig(valueKey);
  const { day, month, year } = useEditFormState((state) => state[valueKey] as IDate) || {};
  const { error, warning } = useFieldValidator(valueKey);
  const dispatch = useEditFormDispatch();

  if (!show) {
    return null;
  }

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        elevation={4}
        TransitionProps={{
          enter: false,
          exit: false,
        }}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Grid className={classes.menuItemContainer}>
          <Grid
            item
            onClick={() => {
              // onChange({ day, month: '', year });
              dispatch(setter({ day, month: '', year }));
              setAnchorEl(null);
            }}
            className={`${classes.menuItem} ${month === '' ? classes.disabledMenu : ''}`}
            style={{
              width: anchorEl
                ? `calc(${window.getComputedStyle(anchorEl)?.width} - 5px)`
                : undefined,
              paddingLeft: 8,
            }}
          />

          {Months.map((monthName: string) => (
            <Grid
              item
              onClick={() => {
                // onChange({ day, month: monthName, year });
                dispatch(setter({ day, month: monthName, year }));
                setAnchorEl(null);
              }}
              className={`${classes.menuItem} ${month === monthName ? classes.disabledMenu : ''}`}
              key={monthName}
              style={{
                width: anchorEl
                  ? `calc(${window.getComputedStyle(anchorEl)?.width} - 5px)`
                  : undefined,
                paddingLeft: 8,
              }}
            >
              {monthName}
            </Grid>
          ))}
        </Grid>
      </Popover>

      <Grid container alignItems="center" className={`${props.rootClass} ${classes.baseRoot}`}>
        <Grid
          item
          className={classes.labelContainer}
          style={{
            alignSelf: 'start',
            marginTop: 5,
          }}
        >
          <Typography className={classes.label}>{label}</Typography>
        </Grid>

        <Grid container justifyContent="space-between" className={classes.inputsWrapper}>
          <Grid item className={classes.inputContainerOuterContainer}>
            <Typography className={classes.inputLabel}>Day</Typography>

            <Grid item className={classes.inputContainer}>
              <InputBase
                placeholder="Day"
                className={classes.input}
                value={day || ''}
                onChange={(e) => {
                  const newDate = parseInt(e.target.value, 10);
                  if (e.target.value === '' || (typeof newDate === 'number' && newDate <= 31)) {
                    // onChange({ day: e.target.value, month, year });
                    dispatch(setter({ day: e.target.value, month, year }));
                  }
                }}
              />
            </Grid>
          </Grid>

          <Grid
            onClick={(e) => {
              setAnchorEl(e.currentTarget as HTMLDivElement);
            }}
            item
            className={classes.inputContainerOuterContainer}
            style={{
              cursor: 'pointer',
              paddingLeft: 0,
            }}
            alignItems="center"
          >
            <Typography className={classes.inputLabel}>Month</Typography>

            <Grid item className={classes.inputContainer} alignItems="center" display="flex">
              <div className={classes.input} style={{ cursor: 'pointer', flex: 1 }}>
                {month || <span className="placeholder">Month</span>}
              </div>

              <ArrowDropDown
                fontSize="small"
                style={{
                  transform: anchorEl ? 'rotate(180deg)' : '',
                  transition: 'transform 0.2s',
                  position: 'absolute',
                  right: 0,
                }}
              />
            </Grid>
          </Grid>

          <Grid item className={classes.inputContainerOuterContainer}>
            <Typography className={classes.inputLabel}>Year</Typography>

            <Grid item className={`${classes.inputContainer} ${warning ? classes.warning : ''}`}>
              <InputBase
                placeholder="Year"
                className={classes.input}
                value={year || ''}
                onChange={(e) => {
                  const newYear = parseInt(e.target.value, 10);
                  if (
                    e.target.value === '' ||
                    (typeof newYear === 'number' && toDay.getFullYear() >= newYear)
                  ) {
                    dispatch(setter({ day, month, year: e.target.value }));
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
